import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { invApi } from "./api";

export const UNITS = ["each", "oz", "lb", "g", "kg", "ml", "L", "cup", "tbsp", "tsp", "slice", "piece", "bag", "case", "bottle", "can"];
export const field = "w-full px-3 py-2.5 rounded-xl border-2 border-stone-200 bg-white text-sm outline-none focus:border-brand-red";
export const label = "text-[10px] uppercase tracking-widest text-stone-500 font-bold";

/** Create/edit an ingredient. `initial` = existing ingredient OR {barcode} for a fresh scan. */
export function IngredientEditor({ token, initial, onSaved, onClose }) {
  const editing = !!initial?.id;
  const [f, setF] = useState({ barcode: "", name: "", unit: "each", current_stock: "", low_stock_threshold: "", cost_per_unit: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF({
      barcode: initial?.barcode || "",
      name: initial?.name || "",
      unit: initial?.unit || "each",
      current_stock: initial?.current_stock ?? "",
      low_stock_threshold: initial?.low_stock_threshold ?? "",
      cost_per_unit: initial?.cost_per_unit ?? "",
    });
  }, [initial]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Name is required");
    const body = {
      barcode: f.barcode.trim(),
      name: f.name.trim(),
      unit: f.unit,
      current_stock: parseFloat(f.current_stock) || 0,
      low_stock_threshold: parseFloat(f.low_stock_threshold) || 0,
      cost_per_unit: f.cost_per_unit === "" ? null : parseFloat(f.cost_per_unit),
    };
    setBusy(true);
    try {
      const { data } = editing
        ? await invApi(token).put(`/inventory/ingredients/${initial.id}`, body)
        : await invApi(token).post("/inventory/ingredients", body);
      toast.success(editing ? "Ingredient updated" : `${data.name} registered`);
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="ingredient-editor">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-5 space-y-3 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest">{editing ? "Edit ingredient" : "Register new ingredient"}</div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-stone-400" data-testid="ingredient-editor-close"><X className="w-5 h-5" /></button>
        </div>
        {!editing && f.barcode && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-900" data-testid="ingredient-editor-new-barcode">
            New barcode <span className="font-mono font-bold">{f.barcode}</span> — tell us what it is.
          </div>
        )}
        <label className="block"><span className={label}>Name</span>
          <input value={f.name} onChange={set("name")} placeholder="e.g. Texas Toast" className={field} data-testid="ingredient-name-input" autoFocus required /></label>
        <label className="block"><span className={label}>Barcode (optional)</span>
          <input value={f.barcode} onChange={set("barcode")} inputMode="numeric" className={`${field} font-mono`} data-testid="ingredient-barcode-input" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><span className={label}>Unit of measure</span>
            <select value={f.unit} onChange={set("unit")} className={field} data-testid="ingredient-unit-select">
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select></label>
          <label className="block"><span className={label}>Low-stock alert at</span>
            <input value={f.low_stock_threshold} onChange={set("low_stock_threshold")} type="number" step="any" min="0" className={field} data-testid="ingredient-threshold-input" /></label>
          <label className="block"><span className={label}>{editing ? "Current stock" : "Initial quantity"}</span>
            <input value={f.current_stock} onChange={set("current_stock")} type="number" step="any" className={field} data-testid="ingredient-stock-input" /></label>
          <label className="block"><span className={label}>Cost per unit ($, optional)</span>
            <input value={f.cost_per_unit} onChange={set("cost_per_unit")} type="number" step="any" min="0" className={field} data-testid="ingredient-cost-input" /></label>
        </div>
        <button type="submit" disabled={busy} data-testid="ingredient-editor-save"
          className="w-full bg-brand-red text-white py-3 rounded-full text-xs uppercase tracking-widest font-black disabled:opacity-50">
          {busy ? "Saving…" : editing ? "Save changes" : "Register & add stock"}
        </button>
      </form>
    </div>
  );
}
